import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line no-restricted-imports -- CSS paylaşımlı, components/TypeMenus/'ta kaldı
import '../../../components/TypeMenus/PanelTypeMenus.css';
import { setCommandType, setOperationData, setCommandMessage } from '../../../redux/operationSlice';
import { COMMANDS } from '../../../utils/commonProperties';
import { selectAllFloorPlans } from '../../../redux/floorPlanSlice';
import { selectAllWires } from '../../../redux/wireSlice';
import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { buildDefaultPanelName } from '../../../utils/panelNaming';

/**
 * CableShaftTypeMenu - Kablo bacası için TypeMenu
 *
 * Fonksiyonlar:
 * 1. Redux'tan kablo bacasına bağlı kabloları listeler
 * 2. Diğer katlardan gelen kabloları gösterir
 * 3. Kullanıcının bir kablo seçip çizime devam etmesini sağlar
 * 4. Kablo bacası bilgilerini gösterir
 */
export default function CableShaftTypeMenu({
  x,
  y,
  onClose,
  cableShaftDetails,
  onDrag,
  scene,
  onContinueWire,
}) {
  const dispatch = useDispatch();
  const floorPlans = useSelector(selectAllFloorPlans);
  const allWires = useSelector(selectAllWires);
  const simpleSupply = useSelector((state) => state.simpleSupply);

  const [showIncoming, setShowIncoming] = useState(true);
  const [showOutgoing, setShowOutgoing] = useState(false);
  const [selectedCable, setSelectedCable] = useState(null);

  const menuRef = useRef(null);
  const dragData = useRef({ active: false, lastX: 0, lastY: 0 });

  const shaftId = cableShaftDetails?.id;
  const shaftGroupId = cableShaftDetails?.shaftGroupId;
  const floorPlanId = cableShaftDetails?.floorPlanId;

 
  // ✅ DEBUG: List ALL cable shafts in scene with their shaftGroupIds (only Groups, not fillMesh/border)
  if (scene) {
    const allShafts = [];
    scene.traverse((obj) => {
      // Sadece Group'ları say, fillMesh ve border'ı atla
      if (obj.userData?.type === 'cableShaft' && !obj.userData?.isFill && !obj.userData?.isBorder) {
        allShafts.push({
          id: obj.userData.id,
          shaftGroupId: obj.userData.shaftGroupId,
          floorPlanId: obj.userData.floorPlanId,
          floorPlanName: obj.userData.floorPlanName,
          autoPlaced: obj.userData.autoPlaced
        });
      }
    });
   }

  // Kat planı adını ID'den bul
  const getFloorPlanName = (fpId) => {
    const fp = floorPlans.find(f => f.id === fpId);
    return fp?.name || fpId || 'Bilinmeyen Kat';
  };

  const floorPlanName = cableShaftDetails?.floorPlanName || getFloorPlanName(floorPlanId);

  // Panel hattı için bu kat plana göre auto-name üret (örn. "1-N.K.T.-1").
  // Sourcecommand DRAW_PANEL ise wire kablo bacasından kat değiştirdiğinde
  // hedef katın isimlendirme kuralı uygulanmalı — kullanıcının "Devam Et"te
  // göreceği isim bu hesaba göre belirlenir.
  const computePanelNameForThisFloor = useMemo(() => {
    return buildDefaultPanelName({
      position: cableShaftDetails?.center,
      floorPlans,
      allComponents: simpleSupply?.components,
    });
  }, [cableShaftDetails?.center, floorPlans, simpleSupply?.components]);

  // Wire adını okunabilir Türkçe metne çevir
  const getDisplayName = (cable) => {
    const sourceCmd = cable.wire?.userData?.sourceCommandType;
    // Panel hattı: bu katın auto-adını göster (kat değiştiyse update edilir)
    if (sourceCmd === COMMANDS.DRAW_PANEL) {
      return computePanelNameForThisFloor || cable.panelName || cable.name || 'Panel';
    }

    const rawName = cable.name || 'Kablo';
    const subType = cable.lightingSubType;
    // lightingSubType varsa onu göster (ör. "Merdiven Aydınlatma")
    if (subType) {
      const isFixture = rawName === 'fixture' || rawName === 'Fixture';
      const isSwitch = rawName === 'lightSwitch' || rawName === 'LightSwitch';
      if (isFixture) return `${subType} (Armatür)`;
      if (isSwitch) return `${subType} (Anahtar)`;
      return subType;
    }
    // lightingSubType yoksa ham isimleri Türkçeleştir
    if (rawName === 'fixture' || rawName === 'Fixture') return 'Armatür Hattı';
    if (rawName === 'lightSwitch' || rawName === 'LightSwitch') return 'Aydınlatma Hattı';
    return rawName;
  };

  // Ok yönünü Türkçe metne çevir
  const getDirectionText = (direction) => {
    switch (direction) {
      case 'up': return '↑ Yukarı';
      case 'down': return '↓ Aşağı';
      case 'left': return '← Sol';
      case 'right': return '→ Sağ';
      default: return direction || '-';
    }
  };

  // Bu kablo bacasına bağlı diğer katlardaki bacaları bul (scene'den)
  const linkedShafts = useMemo(() => {
    if (!scene || !shaftGroupId) return [];

    const shafts = [];
    scene.traverse((obj) => {
      if (obj.userData?.type === 'cableShaft' &&
          obj.userData?.shaftGroupId === shaftGroupId &&
          obj.userData?.id !== shaftId) {
        shafts.push(obj);
      }
    });
    return shafts;
  }, [scene, shaftGroupId, shaftId]);

  // Wire'dan lightingSubType çözümle: önce wire userData, yoksa Redux load'dan bul
  const resolveLightingSubType = (wire) => {
    const direct = wire.userData?.lightingSubType;
    if (direct) return direct;
    // Wire üzerinde yoksa parentLightingLoadId ile Redux'tan bul
    const loadId = wire.userData?.parentLightingLoadId;
    if (loadId && simpleSupply?.components) {
      const load = simpleSupply.components.find(c => c.id === loadId);
      if (load?.metadata?.lightingSubType) return load.metadata.lightingSubType;
    }
    // sourcePanelId ile GT panelinin lighting load'larından bul
    const panelId = wire.userData?.sourcePanelId;
    if (panelId && simpleSupply?.components) {
      const panelLoads = simpleSupply.components.filter(c =>
        c.kind === COMPONENT_KINDS.LOAD &&
        c.parentComponentId === panelId &&
        c.metadata?.lightingSubType
      );
      // Tek çıkış varsa doğrudan al, birden fazlaysa wireName ile eşleştirmeye çalış
      if (panelLoads.length === 1) return panelLoads[0].metadata.lightingSubType;
    }
    return null;
  };

  // ✅ Redux'tan bu kablo bacası grubuna bağlı tüm kabloları al
  const shaftConnectedWires = useMemo(() => {

    if (!shaftGroupId || !allWires) {
       return { incoming: [], outgoing: [], thisFloor: [] };
    }

    const incoming = []; // Diğer katlardan gelen (bu katta devam edilebilir)
    const outgoing = []; // Bu kattan çıkan
    const thisFloor = []; // Bu kattan başlayıp bu katta biten

    allWires.forEach(wire => {
      const conn = wire.userData?.cableShaftConnection;
      if (!conn) {
         return;
      }

      // Wire bu kablo bacası grubuna bağlı mı?
      const startShaft = conn.startShaft;
      const endShaft = conn.endShaft;

      const isConnectedToThisGroup =
        startShaft?.shaftGroupId === shaftGroupId ||
        endShaft?.shaftGroupId === shaftGroupId;

      if (!isConnectedToThisGroup) {
         return;
      }
      const wireFloorPlanId = wire.userData?.floorPlanId;
      const wireName = wire.userData?.wireName || `Kablo`;
      const resolvedSubType = resolveLightingSubType(wire);

      // Kablo bu kattan mı çıkıyor?
      if (wireFloorPlanId === floorPlanId) {
        // Bu kattan çıkan kablo
        outgoing.push({
          wireId: wire.id,
          name: wireName,
          sourceFloorPlanId: wireFloorPlanId,
          sourceFloorPlanName: wire.userData?.floorPlanName,
          arrowDirection: endShaft?.arrowDirection || startShaft?.arrowDirection,
          connectionPoint: endShaft?.connectionPoint || startShaft?.connectionPoint,
          wire: wire,
          sourcePanelId: wire.userData?.sourcePanelId,
          sourcePanelName: wire.userData?.sourcePanelName,
          lightingSubType: resolvedSubType,
          parentLightingLoadId: wire.userData?.parentLightingLoadId,
          panelName: wire.userData?.panelName,
          purposeOfUse: wire.userData?.purposeOfUse,
          aboneTuru: wire.userData?.aboneTuru,
        });
      } else {
        // Diğer kattan gelen kablo
        incoming.push({
          wireId: wire.id,
          name: wireName,
          sourceFloorPlanId: wireFloorPlanId,
          sourceFloorPlanName: wire.userData?.floorPlanName || getFloorPlanName(wireFloorPlanId),
          arrowDirection: endShaft?.arrowDirection || startShaft?.arrowDirection,
          connectionPoint: endShaft?.connectionPoint || startShaft?.connectionPoint,
          wire: wire,
          sourcePanelId: wire.userData?.sourcePanelId,
          sourcePanelName: wire.userData?.sourcePanelName,
          lightingSubType: resolvedSubType,
          parentLightingLoadId: wire.userData?.parentLightingLoadId,
          panelName: wire.userData?.panelName,
          purposeOfUse: wire.userData?.purposeOfUse,
          aboneTuru: wire.userData?.aboneTuru,
        });
      }
    });

    return { incoming, outgoing, thisFloor };
  }, [allWires, shaftGroupId, floorPlanId, getFloorPlanName, simpleSupply]);

  // Kablo seçildiğinde çizime devam et
  const handleContinueWire = (cable) => {
    if (!cable) return;

    // ✅ Kaynak wire'ın command type'ını al (yoksa varsayılan DRAW_WIRE)
    const sourceCommandType = cable.wire?.userData?.sourceCommandType || COMMANDS.DRAW_WIRE;

    // Panel hatları için bu katın auto-adı; diğer hatlarda cable.panelName.
    const resolvedPanelName = sourceCommandType === COMMANDS.DRAW_PANEL
      ? (computePanelNameForThisFloor || cable.panelName)
      : cable.panelName;

    // ✅ Doğru çizim modunu başlat (kaynak wire'ın türüne göre)
    dispatch(setCommandType(sourceCommandType));
    dispatch(setOperationData({
      continueFromShaft: true,
      sourceCable: cable,
      sourceWireId: cable.wireId,
      sourceCommandType: sourceCommandType, // ✅ Command type'ı da geç
      shaftId: shaftId,
      shaftGroupId: shaftGroupId,
      startPoint: cableShaftDetails.center,
      sourceFloorPlanId: cable.sourceFloorPlanId,
      sourceFloorPlanName: cable.sourceFloorPlanName,
      currentFloorPlanName: floorPlanName, // ✅ Kablo bacasının bulunduğu kat (floorArmatures için)
      initialItemType: cable.lightingSubType || null, // ✅ Devam eden hattın türünü aktar
      parentLightingLoadId: cable.parentLightingLoadId || null, // ✅ Fixture hatları için kaynak load ID
      // ✅ Panel çizimi devam bilgileri (kablo bacasından geçen panel hatları için)
      sourcePanelId: cable.sourcePanelId,
      panelName: resolvedPanelName,
      purposeOfUse: cable.purposeOfUse,
      aboneTuru: cable.aboneTuru,
    }));
    dispatch(setCommandMessage(`"${cable.name}" kablosunu bu katta çizmeye devam edin.`));

    if (onContinueWire) {
      onContinueWire(cable);
    }

    onClose();
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    e.stopPropagation();
    dragData.current = {
      active: true,
      lastX: e.clientX,
      lastY: e.clientY,
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragData.current.active) return;
    const dx = e.clientX - dragData.current.lastX;
    const dy = e.clientY - dragData.current.lastY;
    dragData.current.lastX = e.clientX;
    dragData.current.lastY = e.clientY;
    if (onDrag) onDrag(dx, dy);
  };

  const handleMouseUp = () => {
    dragData.current.active = false;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const totalCables = shaftConnectedWires.incoming.length + shaftConnectedWires.outgoing.length;

  return (
    <div
      ref={menuRef}
      className="panel-type-menu"
      style={{ left: x, top: y, minWidth: 300 }}
    >
      {/* Header */}
      <div
        onMouseDown={handleMouseDown}
        className="panel-type-menu-header"
      >
        <span>Kablo Bacası - {floorPlanName}</span>
        <button
          className="close-button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
        >
          ×
        </button>
      </div>

      <div className="panel-type-options">
        {/* Baca Bilgileri */}
        <div className="panel-type-details-box" style={{ maxHeight: 'none', padding: '8px' }}>
          <table className="panel-type-details-table compact-table">
            <tbody>
              <tr>
                <td className="label-cell">Kat</td>
                <td>{floorPlanName}</td>
              </tr>
              <tr>
                <td className="label-cell">Boyut</td>
                <td>{cableShaftDetails?.width?.toFixed(0) || '?'} x {cableShaftDetails?.height?.toFixed(0) || '?'} cm</td>
              </tr>
              <tr>
                <td className="label-cell">Bağlı Kat</td>
                <td>{linkedShafts.length + 1} kat</td>
              </tr>
              <tr>
                <td className="label-cell">Toplam Kablo</td>
                <td>{totalCables} adet</td>
              </tr>
            </tbody>
          </table>
        </div>

        <hr />

        {/* Diğer Katlardan Gelen Kablolar (devam edilebilir) */}
        <button
          onClick={() => setShowIncoming(!showIncoming)}
          className="my-button"
          style={{ width: '100%', textAlign: 'left' }}
        >
          Diğer Katlardan Gelen ({shaftConnectedWires.incoming.length}) {showIncoming ? '▴' : '▾'}
        </button>

        {showIncoming && (
          <div className="panel-type-details-box" style={{ maxHeight: 200, overflowY: 'auto' }}>
            {shaftConnectedWires.incoming.length === 0 ? (
              <div style={{ padding: '8px', color: '#888', textAlign: 'center' }}>
                Henüz diğer katlardan gelen kablo yok
              </div>
            ) : (
              <table className="panel-type-details-table compact-table">
                <tbody>
                  {shaftConnectedWires.incoming.map((cable, idx) => (
                    <tr
                      key={cable.wireId || idx}
                      onClick={() => setSelectedCable(cable)}
                      style={{
                        cursor: 'pointer',
                        background: selectedCable?.wireId === cable.wireId ? '#444' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '6px' }}>
                        <div style={{ fontWeight: 500 }}>{getDisplayName(cable)}</div>
                        {cable.sourcePanelName && (
                          <div style={{ fontSize: '11px', color: '#aaa' }}>
                            {cable.sourcePanelName}
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          {cable.sourceFloorPlanName} {getDirectionText(cable.arrowDirection)}
                        </div>
                      </td>
                      <td style={{ width: 70, textAlign: 'right' }}>
                        <button
                          className="submit-button"
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContinueWire(cable);
                          }}
                        >
                          Devam Et
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Bu Kattan Çıkan Kablolar */}
        <button
          onClick={() => setShowOutgoing(!showOutgoing)}
          className="my-button"
          style={{ width: '100%', textAlign: 'left', marginTop: '4px' }}
        >
          Bu Kattan Çıkan ({shaftConnectedWires.outgoing.length}) {showOutgoing ? '▴' : '▾'}
        </button>

        {showOutgoing && (
          <div className="panel-type-details-box" style={{ maxHeight: 200, overflowY: 'auto' }}>
            {shaftConnectedWires.outgoing.length === 0 ? (
              <div style={{ padding: '8px', color: '#888', textAlign: 'center' }}>
                Bu kattan çıkan kablo yok
              </div>
            ) : (
              <table className="panel-type-details-table compact-table">
                <tbody>
                  {shaftConnectedWires.outgoing.map((cable, idx) => (
                    <tr key={cable.wireId || idx}>
                      <td style={{ padding: '6px' }}>
                        <div style={{ fontWeight: 500 }}>{getDisplayName(cable)}</div>
                        {cable.sourcePanelName && (
                          <div style={{ fontSize: '11px', color: '#aaa' }}>
                            {cable.sourcePanelName}
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          {getDirectionText(cable.arrowDirection)}
                        </div>
                      </td>
                      <td style={{ width: 70, textAlign: 'right' }}>
                        <button
                          className="submit-button"
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContinueWire(cable);
                          }}
                        >
                          Devam Et
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <hr />

        {/* Seçili kablo için devam butonu */}
        {selectedCable && (
          <div style={{ padding: '8px' }}>
            <button
              className="submit-button"
              style={{ width: '100%' }}
              onClick={() => handleContinueWire(selectedCable)}
            >
              "{selectedCable.name}" ile Çizime Devam Et
            </button>
          </div>
        )}

        {/* Yeni kablo ekleme */}
        <div style={{ padding: '8px' }}>
          <button
            className="submit-button"
            style={{ width: '100%', background: '#4a7c4a' }}
            onClick={() => {
              dispatch(setCommandType(COMMANDS.DRAW_WIRE));
              dispatch(setOperationData({
                startFromShaft: true,
                shaftId: shaftId,
                shaftGroupId: shaftGroupId,
                startPoint: cableShaftDetails.center,
                floorPlanId: floorPlanId,
                floorPlanName: floorPlanName,
              }));
              dispatch(setCommandMessage('Kablo bacasından yeni kablo çizmeye başlayın.'));
              onClose();
            }}
          >
            Yeni Kablo Başlat
          </button>
        </div>
      </div>
    </div>
  );
}
